import { useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { Eye, EyeOff, Loader2, ArrowRight } from "lucide-react";
import logo from "../../assets/valuate-logo.png";

const API_BASE_URL =
  import.meta.env.VITE_API_BASE_URL || "https://valuate-api.vercel.app/api/v1";

type Stage = "set-password" | "done" | "error" | "no-token";

/* ─── Global styles + fonts ──────────────────────────────────────────────── */
const Fonts = () => (
  <style>{`
    @import url('https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,600;0,700;1,600&family=DM+Sans:wght@300;400;500;600&display=swap');

    *, *::before, *::after { box-sizing: border-box; margin: 0; padding: 0; }

    /* ── Page root ── */
    .ve-root {
      font-family: 'DM Sans', sans-serif;
      min-height: 100vh;
      display: flex;
      align-items: center;
      justify-content: center;
      background: #eef0f3;
      padding: 1.5rem;
    }

    /* ── Outer card ── */
    .ve-card {
      display: flex;
      width: 100%;
      max-width: 900px;
      border-radius: 20px;
      overflow: hidden;
      box-shadow: 0 8px 48px rgba(0,0,0,0.13);
    }

    /* ── Left panel ── */
    .ve-left {
      width: 50%;
      flex-shrink: 0;
      background: #414961;
      display: flex;
      flex-direction: column;
      justify-content: space-between;
      padding: 2.5rem;
      position: relative;
      overflow: hidden;
    }
    .ve-left-grid {
      position: absolute; inset: 0; pointer-events: none;
      background-image:
        linear-gradient(rgba(255,255,255,0.035) 1px, transparent 1px),
        linear-gradient(90deg, rgba(255,255,255,0.035) 1px, transparent 1px);
      background-size: 44px 44px;
    }
    .ve-left-glow {
      position: absolute; inset: 0; pointer-events: none;
      background:
        radial-gradient(ellipse 65% 55% at 15% 85%, rgba(59,130,246,0.2) 0%, transparent 60%),
        radial-gradient(ellipse 45% 40% at 85% 15%, rgba(99,102,241,0.14) 0%, transparent 60%);
    }
    .ve-left-inner { position: relative; z-index: 1; }

    /* ── Right panel ── */
    .ve-right {
      width: 50%;
      background: #f7f6f3;
      display: flex;
      align-items: center;
      justify-content: center;
      padding: 2.5rem 2rem;
    }
    .ve-right-inner { width: 100%; max-width: 340px; }
    .ve-form-card {
      background: #fff;
      border-radius: 16px;
      box-shadow: 0 2px 8px rgba(0,0,0,0.04), 0 12px 40px rgba(0,0,0,0.07);
      padding: 2rem 2rem 1.75rem;
      animation: fadeUp 0.3s ease both;
    }

    /* ── Tablet (≤ 768px): stack vertically ── */
    @media (max-width: 768px) {
      .ve-root { padding: 1rem; }
      .ve-card { flex-direction: column; max-width: 460px; border-radius: 16px; }
      .ve-left { width: 100%; padding: 1.75rem 1.5rem; }
      .ve-left-features { display: none !important; }
      .ve-left-desc { display: none !important; }
      .ve-left-headline { font-size: 1.25rem !important; margin-bottom: 0.25rem !important; }
      .ve-right { width: 100%; padding: 1.5rem; }
      .ve-form-card { padding: 1.5rem; }
    }

    /* ── Small mobile (≤ 420px): edge-to-edge ── */
    @media (max-width: 420px) {
      .ve-root { padding: 0; align-items: stretch; }
      .ve-card { flex-direction: column; border-radius: 0; max-width: 100%; min-height: 100vh; }
      .ve-left { padding: 1.25rem 1rem; }
      .ve-right { padding: 1.25rem 1rem; align-items: flex-start; padding-top: 1.5rem; }
      .ve-form-card { box-shadow: none; background: transparent; padding: 0; }
    }

    /* ── Animations ── */
    @keyframes fadeUp {
      from { opacity: 0; transform: translateY(8px); }
      to   { opacity: 1; transform: translateY(0); }
    }
    @keyframes pop {
      from { transform: scale(0.6); opacity: 0; }
      to   { transform: scale(1);   opacity: 1; }
    }
    @keyframes fillBar {
      from { width: 0%; }
      to   { width: 100%; }
    }
  `}</style>
);

/* ─── Left panel ─────────────────────────────────────────────────────────── */
function LeftPanel() {
  return (
    <div className="ve-left">
      <div className="ve-left-grid" />
      <div className="ve-left-glow" />

      <div className="ve-left-inner">
        {/* brand */}
        <Link
          to="/"
          style={{ position: "relative", zIndex: 1, textDecoration: "none" }}
        >
          <div className="relative z-20 w-full text-white text-center">
            <img
              src={logo}
              alt="Property Valuation"
              className="w-full h-32 object-cover rounded-lg shadow-lg mb-4"
            />

            <div className="mb-3"></div>
          </div>
        </Link>

        {/* Headline */}
        <h2
          className="ve-left-headline"
          style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "1.6rem", fontWeight: 600,
            color: "#fff", lineHeight: 1.3,
            marginBottom: "0.875rem", letterSpacing: "-0.02em",
          }}
        >
          Secure your{" "}
          <em style={{ fontStyle: "italic", color: "#93c5fd" }}>account</em>{" "}
          in seconds.
        </h2>

        {/* Description */}
        <p
          className="ve-left-desc"
          style={{
            fontSize: "0.875rem",
            color: "rgba(255,255,255,0.45)",
            lineHeight: 1.7, marginBottom: "2rem",
          }}
        >
          Verify your email and set a strong password to unlock full access to
          the Valuate platform.
        </p>

        {/* Feature list */}
        <div className="ve-left-features">
          {[
            { dot: "#3b82f6", label: "End-to-end encrypted" },
            { dot: "#6366f1", label: "Real-time valuation data" },
            { dot: "#8b5cf6", label: "Trusted by 500+ firms" },
          ].map(({ dot, label }) => (
            <div key={label} style={{ display: "flex", alignItems: "center", gap: "0.625rem", marginBottom: "0.625rem" }}>
              <span style={{ width: 8, height: 8, borderRadius: "50%", background: dot, flexShrink: 0 }} />
              <span style={{ fontSize: "0.8125rem", color: "rgba(255,255,255,0.5)", fontWeight: 400 }}>
                {label}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Footer */}
      <p className="ve-left-inner" style={{ fontSize: "0.7rem", color: "rgba(255,255,255,0.2)", marginTop: "1rem" }}>
        © {new Date().getFullYear()} Valuate. All rights reserved.
      </p>
    </div>
  );
}

/* ─── Right shell ─────────────────────────────────────────────────────────── */
function RightShell({ children }: { children: React.ReactNode }) {
  return (
    <div className="ve-right">
      <div className="ve-right-inner">
        <div className="ve-form-card">{children}</div>
      </div>
    </div>
  );
}

/* ─── Shared page wrapper ────────────────────────────────────────────────── */
function Page({ children }: { children: React.ReactNode }) {
  return (
    <>
      <Fonts />
      <div className="ve-root">
        <div className="ve-card">
          <LeftPanel />
          <RightShell>{children}</RightShell>
        </div>
      </div>
    </>
  );
}

/* ─── Card heading ───────────────────────────────────────────────────────── */
function CardHeading({ title, subtitle }: { title: string; subtitle: string }) {
  return (
    <div style={{ marginBottom: "1.5rem" }}>
      <h2 style={{
        fontFamily: "'Playfair Display', serif",
        fontSize: "1.35rem", fontWeight: 700,
        color: "#0f172a", margin: "0 0 0.4rem",
        letterSpacing: "-0.02em",
      }}>
        {title}
      </h2>
      <p style={{ fontSize: "0.84rem", color: "#64748b", lineHeight: 1.6, margin: 0 }}>
        {subtitle}
      </p>
    </div>
  );
}

/* ─── Input field ────────────────────────────────────────────────────────── */
function Field({
  label, type = "text", value, onChange, onFocus, onBlur,
  placeholder, active, rightEl, required,
}: {
  label: string; type?: string; value: string;
  onChange: (v: string) => void; onFocus: () => void; onBlur: () => void;
  placeholder?: string; active: boolean;
  rightEl?: React.ReactNode; required?: boolean;
}) {
  return (
    <div>
      <label style={{
        display: "block", fontSize: "0.8125rem", fontWeight: 500,
        color: "#374151", marginBottom: "0.35rem",
      }}>
        {label}
      </label>
      <div style={{ position: "relative" }}>
        <input
          type={type}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={onFocus}
          onBlur={onBlur}
          placeholder={placeholder}
          required={required}
          style={{
            width: "100%",
            padding: rightEl ? "0.75rem 2.5rem 0.75rem 0.875rem" : "0.75rem 0.875rem",
            borderRadius: 10,
            border: `1.5px solid ${active ? "#3b82f6" : "#e2e8f0"}`,
            outline: "none",
            fontSize: "0.875rem", color: "#0f172a", background: "#fff",
            boxShadow: active ? "0 0 0 3px rgba(59,130,246,0.1)" : "none",
            transition: "border-color 0.15s, box-shadow 0.15s",
          }}
        />
        {rightEl && (
          <div style={{ position: "absolute", right: 12, top: "50%", transform: "translateY(-50%)" }}>
            {rightEl}
          </div>
        )}
      </div>
    </div>
  );
}

/* ─── Error banner ───────────────────────────────────────────────────────── */
function ErrorBanner({ message }: { message: string }) {
  return (
    <div style={{
      background: "#fef2f2", border: "1px solid #fecaca",
      borderRadius: 10, padding: "0.7rem 0.875rem",
      marginBottom: "1rem", fontSize: "0.8125rem",
      color: "#991b1b", lineHeight: 1.5,
    }}>
      {message}
    </div>
  );
}

/* ─── Primary button ─────────────────────────────────────────────────────── */
function PrimaryButton({ loading, label, loadingLabel }: {
  loading: boolean; label: string; loadingLabel: string;
}) {
  return (
    <button
      type="submit"
      disabled={loading}
      style={{
        width: "100%", padding: "0.8125rem", marginTop: "0.25rem",
        background: loading ? "#e2e8f0" : "linear-gradient(135deg, #1d4ed8 0%, #4338ca 100%)",
        color: loading ? "#94a3b8" : "#fff",
        border: "none", borderRadius: 10,
        fontSize: "0.875rem", fontWeight: 600,
        cursor: loading ? "not-allowed" : "pointer",
        display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem",
        boxShadow: loading ? "none" : "0 4px 14px rgba(59,130,246,0.28)",
        transition: "opacity 0.15s",
      }}
      onMouseEnter={(e) => { if (!loading) e.currentTarget.style.opacity = "0.9"; }}
      onMouseLeave={(e) => { e.currentTarget.style.opacity = "1"; }}
    >
      {loading
        ? <><Loader2 size={15} className="animate-spin" />{loadingLabel}</>
        : <>{label}<ArrowRight size={15} /></>}
    </button>
  );
}

/* ─── Back to login ──────────────────────────────────────────────────────── */
function BackToLogin() {
  return (
    <p style={{ marginTop: "1.25rem", textAlign: "center", fontSize: "0.8125rem", color: "#94a3b8" }}>
      <Link to="/login" style={{ color: "#4f46e5", fontWeight: 500, textDecoration: "none" }}>
        ← Back to Login
      </Link>
    </p>
  );
}

/* ─── Password strength bar ──────────────────────────────────────────────── */
function StrengthBar({ pwd }: { pwd: string }) {
  if (!pwd) return null;
  const color = pwd.length < 6 ? "#ef4444" : pwd.length < 10 ? "#f59e0b" : "#22c55e";
  return (
    <div style={{ display: "flex", gap: 4, marginTop: 6 }}>
      {[1, 2, 3, 4].map((i) => (
        <div key={i} style={{
          flex: 1, height: 3, borderRadius: 99,
          background: pwd.length >= i * 2 ? color : "#e2e8f0",
          transition: "background 0.2s",
        }} />
      ))}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════════
   MAIN PAGE
═══════════════════════════════════════════════════════════════════════════ */
export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const token = searchParams.get("token");

  const [stage, setStage] = useState<Stage>(token ? "set-password" : "no-token");
  const [error, setError] = useState<string | null>(null);

  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [activeInput, setActiveInput] = useState("");

  const [email, setEmail] = useState("");
  const [resending, setResending] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [showResendForm, setShowResendForm] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirmPassword) { setError("Passwords do not match."); return; }
    setError(null);
    setSubmitting(true);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/verify-email`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (res.ok) {
        setStage("done");
        setTimeout(() => navigate("/login", { state: { message: "Account verified! You can now log in." } }), 3000);
      } else {
        setError(data.message || "Verification failed. The link may be invalid or expired.");
        setStage("error");
      }
    } catch {
      setError("Network error. Please check your connection and try again.");
      setStage("error");
    } finally {
      setSubmitting(false);
    }
  };

  const handleResend = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email) { setError("Please enter your email address."); return; }
    setResending(true);
    setError(null);
    setResendSuccess(false);
    try {
      const res = await fetch(`${API_BASE_URL}/auth/resend-verification`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ email }),
      });
      const data = await res.json();
      if (res.ok) {
        setResendSuccess(true);
        setEmail("");
        setTimeout(() => { setResendSuccess(false); setShowResendForm(false); }, 3000);
      } else {
        setError(data.message || "Failed to resend. Please try again.");
      }
    } catch {
      setError("Network error. Please try again.");
    } finally {
      setResending(false);
    }
  };

  const eyeBtn = (
    <button
      type="button"
      onClick={() => setShowPwd((v) => !v)}
      style={{ background: "none", border: "none", cursor: "pointer", padding: 0, color: "#94a3b8", display: "flex" }}
    >
      {showPwd ? <EyeOff size={16} /> : <Eye size={16} />}
    </button>
  );

  /* ── Stage: set-password ── */
  if (stage === "set-password") {
    return (
      <Page>
        <CardHeading
          title="Verify Email & Set Password"
          subtitle="Choose a password to complete your account setup."
        />
        {error && <ErrorBanner message={error} />}
        <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <div>
            <Field
              label="New Password"
              type={showPwd ? "text" : "password"}
              value={password}
              onChange={setPassword}
              onFocus={() => setActiveInput("password")}
              onBlur={() => setActiveInput("")}
              placeholder="At least 8 characters"
              active={activeInput === "password"}
              rightEl={eyeBtn}
              required
            />
            <StrengthBar pwd={password} />
          </div>
          <div>
            <Field
              label="Confirm Password"
              type={showPwd ? "text" : "password"}
              value={confirmPassword}
              onChange={setConfirmPassword}
              onFocus={() => setActiveInput("confirm")}
              onBlur={() => setActiveInput("")}
              placeholder="Repeat your password"
              active={activeInput === "confirm"}
              required
            />
            {confirmPassword.length > 0 && (
              <p style={{ fontSize: "0.75rem", marginTop: "0.3rem", color: password === confirmPassword ? "#16a34a" : "#dc2626" }}>
                {password === confirmPassword ? "✓ Passwords match" : "✗ Passwords don't match"}
              </p>
            )}
          </div>
          <PrimaryButton loading={submitting} label="Verify & Set Password" loadingLabel="Verifying…" />
        </form>
        <BackToLogin />
      </Page>
    );
  }

  /* ── Stage: done ── */
  if (stage === "done") {
    return (
      <Page>
        <style>{`
          @keyframes pop { from{transform:scale(0.6);opacity:0} to{transform:scale(1);opacity:1} }
          @keyframes fillBar { from{width:0%} to{width:100%} }
        `}</style>
        <div style={{ textAlign: "center", padding: "0.5rem 0" }}>
          <div style={{
            width: 72, height: 72,
            background: "linear-gradient(135deg, #f0fdf4, #dcfce7)",
            border: "1px solid #bbf7d0",
            borderRadius: "50%",
            display: "flex", alignItems: "center", justifyContent: "center",
            margin: "0 auto 1.25rem",
            animation: "pop 0.4s cubic-bezier(0.34,1.56,0.64,1) both",
          }}>
            <svg width="32" height="32" viewBox="0 0 24 24" fill="none"
              stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <polyline points="20 6 9 17 4 12" />
            </svg>
          </div>
          <h2 style={{
            fontFamily: "'Playfair Display', serif",
            fontSize: "1.35rem", fontWeight: 700,
            color: "#0f172a", margin: "0 0 0.5rem", letterSpacing: "-0.02em",
          }}>
            You're all set!
          </h2>
          <p style={{ fontSize: "0.875rem", color: "#64748b", lineHeight: 1.6, marginBottom: "1.5rem" }}>
            Your account is verified and password saved.<br />Redirecting you to login…
          </p>
          <div style={{ height: 3, background: "#e2e8f0", borderRadius: 99, marginBottom: "1.5rem", overflow: "hidden" }}>
            <div style={{
              height: "100%",
              background: "linear-gradient(90deg, #3b82f6, #6366f1)",
              borderRadius: 99,
              animation: "fillBar 3s linear forwards",
            }} />
          </div>
          <Link to="/login" style={{
            display: "inline-flex", alignItems: "center", gap: "0.5rem",
            padding: "0.75rem 1.5rem",
            background: "linear-gradient(135deg, #1d4ed8 0%, #4338ca 100%)",
            color: "#fff", borderRadius: 10,
            fontSize: "0.875rem", fontWeight: 600,
            textDecoration: "none",
            boxShadow: "0 4px 14px rgba(59,130,246,0.28)",
          }}>
            Go to Login <ArrowRight size={15} />
          </Link>
        </div>
        <BackToLogin />
      </Page>
    );
  }

  /* ── Stage: error | no-token ── */
  return (
    <Page>
      <CardHeading
        title={stage === "error" ? "Verification Failed" : "Check Your Inbox"}
        subtitle={
          stage === "error"
            ? (error ?? "The link may be invalid or expired.")
            : "We sent a verification link to your email. Didn't receive it?"
        }
      />

      {error && stage !== "error" && <ErrorBanner message={error} />}

      {resendSuccess && (
        <div style={{
          background: "#f0fdf4", border: "1px solid #bbf7d0",
          borderRadius: 10, padding: "0.7rem 0.875rem",
          marginBottom: "1rem", fontSize: "0.8125rem",
          color: "#166534", fontWeight: 500,
        }}>
          ✓ Verification email sent! Check your inbox.
        </div>
      )}

      {showResendForm ? (
        <form onSubmit={handleResend} style={{ display: "flex", flexDirection: "column", gap: "1rem" }}>
          <Field
            label="Email Address"
            type="email"
            value={email}
            onChange={setEmail}
            onFocus={() => setActiveInput("email")}
            onBlur={() => setActiveInput("")}
            placeholder="Enter your email address"
            active={activeInput === "email"}
            required
          />
          <PrimaryButton loading={resending} label="Resend Verification Email" loadingLabel="Sending…" />
          <button
            type="button"
            onClick={() => setShowResendForm(false)}
            style={{
              background: "none", border: "none", cursor: "pointer",
              fontSize: "0.8125rem", color: "#94a3b8",
              textAlign: "center", padding: "0.25rem",
            }}
          >
            Cancel
          </button>
        </form>
      ) : (
        <button
          onClick={() => setShowResendForm(true)}
          style={{
            width: "100%", padding: "0.8125rem",
            background: "linear-gradient(135deg, #1d4ed8 0%, #4338ca 100%)",
            color: "#fff", border: "none", borderRadius: 10,
            fontSize: "0.875rem", fontWeight: 600, cursor: "pointer",
            display: "flex", alignItems: "center", justifyContent: "center", gap: "0.5rem",
            boxShadow: "0 4px 14px rgba(59,130,246,0.28)",
            transition: "opacity 0.15s",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.9")}
          onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
        >
          Resend Verification Email
        </button>
      )}

      <BackToLogin />
    </Page>
  );
}